import { reportOperationError, operationBreadcrumb } from '../../../lib/observability.ts'
import { sileo } from 'sileo'
import type { CashSession, SessionTicketRecord, TenantContext } from '../../../types'
import { getPrintAgentErrorMessage } from '../api/PrintAgentError'
import { nextPrintCopyNumber } from './printAgentStorage'
import { printCompletedSale } from './printCompletedSale'
import { usePrintAgentStore } from '../store/usePrintAgentStore'
import { getAutomaticSaleHardwareAction } from './cashDrawerRules'
import { getPrintFailurePatch } from './printFailure'

type PrintTicketOptions = {
  context: TenantContext
  cashSession: CashSession | null
  payload: SessionTicketRecord['payload']
  tickets: SessionTicketRecord[]
  updateTicketPrintState: (saleId: string, patch: Partial<Pick<SessionTicketRecord,
    'printStatus' | 'printJobId' | 'printRequestId' | 'printedAt' | 'printErrorCode' | 'printAttempts'>>) => void
  options?: { isReprint?: boolean; copyNumber?: number; cashDrawerAlreadyRequested?: boolean }
}

export async function printTicket({ cashSession, context, payload, tickets, updateTicketPrintState, options = {} }: PrintTicketOptions) {
  const printState = usePrintAgentStore.getState()
  const requestId = options.isReprint ? `print:${payload.sale.id}:copy:${options.copyNumber || 1}` : `print:${payload.sale.id}:original`
  operationBreadcrumb({ operation: 'sale.print', saleId: payload.sale.id, ticketId: payload.ticket.id, operationId: requestId, step: 'requested' })
  const payments = payload.payments ?? (payload.payment ? [payload.payment] : [])
  const hardwareAction = getAutomaticSaleHardwareAction({
    payments,
    isReprint: options.isReprint,
    settings: { ...printState.preferences, cashlogyConfigured: printState.cashlogyConfigured },
  })
  if (hardwareAction !== 'print') {
    updateTicketPrintState(payload.sale.id, {
      printStatus: 'not_requested', printRequestId: null, printErrorCode: null,
    })
    if (hardwareAction === 'none') return
    if (options.cashDrawerAlreadyRequested) return
    if (!printState.token || !printState.selectedPrinterId) {
      sileo.warning({ title: 'Venta completada, pero no se ha podido abrir el cajón', description: 'Configura el servidor y la impresora desde Ajustes > Hardware > Impresión.' })
      return
    }
    try {
      await printState.openCashDrawer({
        requestId: `drawer:${payload.sale.id}:payment`,
        printerId: printState.selectedPrinterId,
      })
      sileo.success({ title: 'Cajón abierto' })
    } catch (error) {
    reportOperationError(error, { operation: 'sale.print', integration: 'print-agent', operationId: requestId, saleId: payload.sale.id, ticketId: payload.ticket.id, cashSessionId: cashSession?.id, step: 'drawer' })
      sileo.warning({ title: 'La venta se ha completado, pero el cajón no se ha podido abrir', description: getPrintAgentErrorMessage(error) })
    }
    return
  }
  if (!printState.token || !printState.selectedPrinterId) {
    updateTicketPrintState(payload.sale.id, { printStatus: 'not_requested', printRequestId: requestId })
    sileo.warning({ title: 'Venta completada sin imprimir', description: 'Configura el servidor y la impresora desde Ajustes > Hardware > Impresión.' })
    return
  }
  updateTicketPrintState(payload.sale.id, {
    printStatus: 'pending', printRequestId: requestId, printErrorCode: null,
    printAttempts: (tickets.find((ticket) => ticket.id === payload.sale.id)?.printAttempts || 0) + 1,
  })
  try {
    const job = await printCompletedSale({
      sale: payload,
      context,
      establishment: {
        name: context.venueName,
        legalName: payload.localFiscal?.issuerName ?? context.venueLegalName,
        taxId: payload.localFiscal?.issuerNif ?? context.venueTaxId,
        address: payload.localFiscal?.issuerAddress ?? context.venueAddress,
        timezone: context.venueTimeZone,
        cashRegisterName: cashSession?.cashRegisterName,
        employeeName: context.userName,
      },
      isReprint: options.isReprint, copyNumber: options.copyNumber, cashDrawerAlreadyRequested: options.cashDrawerAlreadyRequested,
    })
    updateTicketPrintState(payload.sale.id, {
      printStatus: 'printed', printJobId: job.jobId || job.id || null,
      printRequestId: job.requestId || requestId, printedAt: job.printedAt || new Date().toISOString(), printErrorCode: null,
    })
    sileo.success({ title: options.isReprint ? 'Copia impresa correctamente' : payload.ticket.invoice ? 'Factura impresa correctamente' : 'Ticket impreso correctamente' })
  } catch (error) {
    reportOperationError(error, { operation: 'sale.print', integration: 'print-agent', operationId: requestId, saleId: payload.sale.id, ticketId: payload.ticket.id, cashSessionId: cashSession?.id, step: hardwareAction === 'print' ? 'ticket' : 'drawer' })
    const code = error && typeof error === 'object' && 'code' in error ? String(error.code) : 'PRINT_FAILED'
    updateTicketPrintState(payload.sale.id, getPrintFailurePatch(code, requestId))
    sileo.warning({ title: 'La venta se ha completado, pero el ticket no se ha podido imprimir', description: getPrintAgentErrorMessage(error) })
  }
}

export function getReprintCopyNumber(ticketId: string) {
  const scope = usePrintAgentStore.getState().scope
  if (!scope) return null
  return nextPrintCopyNumber(scope, ticketId)
}

export function mergeRemoteTicketPrintStates(localTickets: SessionTicketRecord[], remoteTickets: SessionTicketRecord[]) {
  const localById = new Map(localTickets.map((ticket) => [ticket.id, ticket]))
  return remoteTickets.map((ticket) => {
    const local = localById.get(ticket.id)
    return local ? { ...ticket, printStatus: local.printStatus, printJobId: local.printJobId, printRequestId: local.printRequestId, printedAt: local.printedAt, printErrorCode: local.printErrorCode, printAttempts: local.printAttempts } : ticket
  })
}
